/**
 * Предписания карантина — ПЕРЕИСПОЛЬЗУЕМЫЙ виджет (решение владельца 08.10):
 * агроцикл, хозяйство, район (страница «Карантин»), контур и «Мой день».
 * Это работы, созданные карантином, а не все работы: в процент выполнения
 * плана они не входят (S4, К9). Один виджет везде (agroai-0e8): бывший блок
 * «Предписания» экрана «Карантин» (таблица по контурам с историей случая)
 * влит сюда и удалён.
 *
 * Область — проп `scope`: `{ cycle }` | `{ actorId }` | `{ contourId }` | `{}`
 * (район); поля можно сочетать (цикл + хозяйство). У контура тепловой карты
 * нет (один контур) — там только карта состояния.
 *
 * Фильтры: «открытые / закрытые / все», цвет случая (красный / жёлтый /
 * устранено), поиск по хозяйству, контуру или виду, порядок «по тяжести /
 * по сроку»; вид «таблица / карта / частота»; догрузка страницами. Фильтры и
 * порядок считает сервер — страница берётся из уже отфильтрованного списка.
 *
 * Строка (первый слой): полоса цвета случая, контур и часть, хозяйство, вид и
 * класс A1/A2/A3, этап случая коротко, срок и «после срока», площадь
 * поражённой части. Раскрытие — история случая (`PrescriptionCase`): этапы на
 * таймлайне и карточки деталей (инспектор, акты, документы, работа в
 * агроцикле), полное дело контура с вводом инспектора — по нажатию.
 *
 * Цвет случая один везде — статус единицы земли: красный — красный случай
 * (A1/A2), жёлтый — под наблюдением, зелёный — устранено замером. Просрочка —
 * отдельный признак: метка «после срока» в строке и пунктирная обводка на
 * карте (цвет не меняет). Карта красит открытые КРАСНЫМ и ЖЁЛТЫМ раздельно:
 * жёлтый — тон `progress` (жёлтый шкалы), а не оранжевый `warning`, который
 * на точках района сливался с красным; точки на отдалении — у обоих.
 * «Частота» — последовательная шкала числа предписаний на контуре за период
 * с порогами-квантилями области, рецидив — обводкой.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlarmClock, ChevronRight, Flame, List, Map as MapIcon, Search } from 'lucide-react';
import { request, type Schemas } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { useAnalysisContext } from '../../../analysis/context';
import { Badge } from '../../ui/Badge';
import { Card } from '../../ui/Card';
import { Icon } from '../../ui/Icon';
import { LoadError, failed } from '../../ui/LoadError';
import { SkeletonList } from '../../ui';
import { MapWidget } from '../../map/MapWidget';
import type { MapLayerData } from '../../map/layers';
import { countOf, dateShort, num, pctValue, WORDS } from '../../lib/format';
import { classShort, haText, isQuarantineClass, landHaText } from '../../pages/quarantine/quarantineArea';
import { FOLLOWUP_LABEL } from '../../pages/cabinet/model';
import { lazyWords } from '../../lib/lazyWords';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { PrescriptionCase } from './PrescriptionCase';
import { binLabel, caseStage, caseTone, contourTone, heatBinOf, heatBins, type CaseTone } from './prescriptionsModel';
import './prescriptionsWidget.css';

const m = defineMessages({
  wsReported: { id: 'widget.prescriptions.work.reported', defaultMessage: 'отчёт хозяйства получен', description: 'Состояние работы по предписанию: отчёт хозяйства получен (это не устранение); строчными' },
  wsInProgress: { id: 'widget.prescriptions.work.inProgress', defaultMessage: 'в работе', description: 'Состояние работы по предписанию: работа идёт; строчными' },
  wsOverdue: { id: 'widget.prescriptions.work.overdue', defaultMessage: 'срок работы прошёл, отчёта нет', description: 'Состояние работы по предписанию: срок прошёл, отчёта нет; строчными' },
  wsInWindow: { id: 'widget.prescriptions.work.inWindow', defaultMessage: 'в сроке, отчёта нет', description: 'Состояние работы по предписанию: срок идёт, отчёта пока нет; строчными' },
  wsAwaiting: { id: 'widget.prescriptions.work.awaiting', defaultMessage: 'срок ещё не начался', description: 'Состояние работы по предписанию: окно работы ещё не открылось; строчными' },
  wsNoData: { id: 'widget.prescriptions.work.noData', defaultMessage: 'отчёта нет', description: 'Состояние работы по предписанию: отчёта нет; строчными' },
  title: { id: 'widget.prescriptions.title', defaultMessage: 'Предписания карантина', description: 'Заголовок виджета предписаний карантина на странице агроцикла' },
  loadWhat: { id: 'widget.prescriptions.loadWhat', defaultMessage: 'предписания карантина', description: 'Что не загрузилось, в винительном падеже' },
  summary: {
    id: 'widget.prescriptions.summaryV2',
    defaultMessage: '{open} открыто · {closed} устранено замером{hasSignal, select, yes { · {signal} под сигналом} other {}}{hasOverdue, select, yes { · после срока {overdue, plural, one {# участок} few {# участка} many {# участков} other {# участка}}} other {}}{hasPct, select, yes { · устранено в срок {pct} % площади} other {}}',
    description: 'Сводка виджета предписаний; {open}/{closed} — числа предписаний, {signal} — «12 га» земли под сигналом, {overdue} — число УЧАСТКОВ после срока (участок — единица земли: контур или его часть; не предписания и не контуры — у KPI «После срока» экрана «Карантин» основа «контуры»), {pct} — процент с одним знаком',
  },
  basisCycle: {
    id: 'widget.prescriptions.basisCycle',
    defaultMessage: 'Основа счёта: по циклу работы предписания. Предписание относится к циклу, в котором выдана его работа, а не к циклу культуры контура.',
    description: 'Подпись под сводкой виджета предписаний на странице агроцикла: к какому циклу отнесено каждое предписание',
  },
  filterAria: { id: 'widget.prescriptions.filterAria', defaultMessage: 'Какие предписания показать', description: 'aria-label переключателя открытые/закрытые/все' },
  fOpen: { id: 'widget.prescriptions.filter.open', defaultMessage: 'Открытые', description: 'Фильтр: открытые предписания (устранение замером не подтверждено)' },
  fClosed: { id: 'widget.prescriptions.filter.closed', defaultMessage: 'Закрытые', description: 'Фильтр: предписания, устранённые замером' },
  fAll: { id: 'widget.prescriptions.filter.all', defaultMessage: 'Все', description: 'Фильтр: все предписания цикла' },
  toneFilterAria: { id: 'widget.prescriptions.toneFilterAria', defaultMessage: 'Цвет случая', description: 'aria-label фильтра по цвету случая (красный/жёлтый/устранено); повторное нажатие снимает фильтр' },
  fRed: { id: 'widget.prescriptions.filter.red', defaultMessage: 'Красные', description: 'Фильтр цвета случая: красный статус (A1/A2)' },
  fYellow: { id: 'widget.prescriptions.filter.yellow', defaultMessage: 'Жёлтые', description: 'Фильтр цвета случая: жёлтый, под наблюдением' },
  fGreen: { id: 'widget.prescriptions.filter.green', defaultMessage: 'Устранено', description: 'Фильтр цвета случая: устранено замером' },
  viewAria: { id: 'widget.prescriptions.viewAria', defaultMessage: 'Вид', description: 'aria-label переключателя таблица/карта' },
  vTable: { id: 'widget.prescriptions.view.table', defaultMessage: 'Таблица', description: 'Вид виджета предписаний: таблица' },
  vMap: { id: 'widget.prescriptions.view.map', defaultMessage: 'Карта', description: 'Вид виджета предписаний: карта' },
  searchPh: { id: 'widget.prescriptions.searchPh', defaultMessage: 'Хозяйство, контур или вид', description: 'Подсказка в поле поиска виджета предписаний' },
  searchPhNoFarm: { id: 'widget.prescriptions.searchPhNoFarm', defaultMessage: 'Контур или вид', description: 'Подсказка в поле поиска предписаний, когда область — одно хозяйство' },
  searchAria: { id: 'widget.prescriptions.searchAria', defaultMessage: 'Поиск предписаний по хозяйству, номеру контура или виду', description: 'aria-label поля поиска предписаний' },
  sortAria: { id: 'widget.prescriptions.sortAria', defaultMessage: 'Порядок строк', description: 'aria-label переключателя порядка строк' },
  sortSeverity: { id: 'widget.prescriptions.sort.severity', defaultMessage: 'По тяжести', description: 'Порядок строк: красные и после срока первыми' },
  sortDeadline: { id: 'widget.prescriptions.sort.deadline', defaultMessage: 'По сроку', description: 'Порядок строк: ближайший срок первым' },
  reset: { id: 'widget.prescriptions.reset', defaultMessage: 'Сбросить цвет и поиск', description: 'Кнопка: снять фильтр цвета и поиск' },
  colToggle: { id: 'widget.prescriptions.col.toggle', defaultMessage: 'История', description: 'Заголовок колонки кнопок раскрытия истории случая; скрыт визуально, для чтения с экрана' },
  colContour: { id: 'widget.prescriptions.col.contour', defaultMessage: 'Контур', description: 'Колонка таблицы предписаний: контур' },
  colFarm: { id: 'widget.prescriptions.col.farm', defaultMessage: 'Хозяйство', description: 'Колонка таблицы предписаний: хозяйство' },
  colObject: { id: 'widget.prescriptions.col.object', defaultMessage: 'Объект и класс', description: 'Колонка таблицы предписаний: вид вредного организма и его класс (A1/A2/A3)' },
  colStage: { id: 'widget.prescriptions.col.stage', defaultMessage: 'Этап случая', description: 'Колонка таблицы предписаний: где сейчас случай (предписание, отчёт, замер, устранено)' },
  colDeadline: { id: 'widget.prescriptions.col.deadline', defaultMessage: 'Срок', description: 'Колонка таблицы предписаний: срок предписания' },
  colArea: { id: 'widget.prescriptions.col.area', defaultMessage: 'Площадь', description: 'Колонка таблицы предписаний: площадь поражённой части (земля под сигналом)' },
  rowToggle: { id: 'widget.prescriptions.rowToggle', defaultMessage: 'История случая на контуре № {no}', description: 'aria-label кнопки раскрытия истории случая в строке; {no} — номер контура' },
  contourNo: { id: 'widget.prescriptions.contourNo', defaultMessage: '№ {no}', description: 'Номер контура-ссылки; {no} — номер' },
  partOf: { id: 'widget.prescriptions.partOf', defaultMessage: 'часть «{part}»', description: 'Под номером контура: на какой части контура случай; {part} — название части' },
  objectUnnamed: { id: 'widget.prescriptions.objectUnnamed', defaultMessage: 'вид не назван', description: 'Ячейка «Объект»: вид вредного организма не назван; строчными' },
  stEliminated: { id: 'widget.prescriptions.stage.eliminated', defaultMessage: 'устранено {date}', description: 'Этап случая в строке: устранено замером или актом; {date} — дата; строчными' },
  stFollowup: { id: 'widget.prescriptions.stage.followup', defaultMessage: 'замер {date}: {outcome}', description: 'Этап случая в строке: последний замер инспектора; {date} — дата, {outcome} — итог («очаг сохранился»)' },
  stReported: { id: 'widget.prescriptions.stage.reported', defaultMessage: 'обработали — ждём замера', description: 'Этап случая в строке: отчёт хозяйства есть, замера нет (это не устранение); строчными' },
  stDecision: { id: 'widget.prescriptions.stage.decision', defaultMessage: 'ждёт решения службы', description: 'Этап случая в строке: нужно решение ответственного; строчными' },
  stNoAddressee: { id: 'widget.prescriptions.stage.noAddressee', defaultMessage: 'адресат неизвестен', description: 'Этап случая в строке: хозяйство-адресат не установлено; строчными' },
  stRequirement: { id: 'widget.prescriptions.stage.requirement', defaultMessage: 'предписание — ждём отчёта', description: 'Этап случая в строке: предписание выдано, отчёта хозяйства нет; строчными' },
  overdue: { id: 'widget.prescriptions.overdue', defaultMessage: 'после срока', description: 'Пометка: срок предписания прошёл; строчными' },
  lateDays: { id: 'widget.prescriptions.lateDays', defaultMessage: 'после срока {days} дн.', description: 'Метка в строке: сколько дней после срока (считает сервер); «дн.» — сокращение «дней»; узкая колонка' },
  noDeadline: { id: 'widget.prescriptions.noDeadline', defaultMessage: 'не назван', description: 'Ячейка «Срок»: срок не назван' },
  more: { id: 'widget.prescriptions.more', defaultMessage: 'Показать ещё {count}', description: 'Кнопка догрузки строк таблицы предписаний; {count} — сколько догрузится' },
  headCount: {
    id: 'widget.prescriptions.headCount',
    defaultMessage: '{works} по {reqs, plural, one {# предписанию} few {# предписаниям} many {# предписаниям} other {# предписаниям}}',
    description: 'Значок в шапке, когда работ больше, чем предписаний (одно предписание на части контура у разных хозяйств — несколько работ); {works} — «545 работ», {reqs} — число предписаний, как у фильтра «Все»',
  },
  shown: {
    id: 'widget.prescriptions.shownV2',
    defaultMessage: 'Показано {shown} из {total}{bySeverity, select, yes {: первыми — красные, после срока и ждущие решения} other {: первыми — ближайший срок}}. Стрелка в начале строки — история случая.',
    description: 'Подпись под таблицей; {shown}/{total} — числа; {bySeverity} — yes при порядке «по тяжести»',
  },
  none: { id: 'widget.prescriptions.none', defaultMessage: 'Предписаний с таким состоянием нет.', description: 'Пустая таблица предписаний под фильтром' },
  vHeat: { id: 'widget.prescriptions.view.heat', defaultMessage: 'Частота', description: 'Вид виджета предписаний: тепловая карта — сколько предписаний получил контур за период' },
  toneRed: { id: 'widget.prescriptions.tone.red', defaultMessage: 'красный случай', description: 'Легенда цвета случая: красный статус карантина (A1/A2, повышенный риск); строчными' },
  toneYellow: { id: 'widget.prescriptions.tone.yellow', defaultMessage: 'жёлтый — под наблюдением', description: 'Легенда цвета случая: жёлтый статус карантина, под наблюдением; строчными' },
  toneGreen: { id: 'widget.prescriptions.tone.green', defaultMessage: 'устранено замером', description: 'Легенда цвета случая: зелёный — устранено, подтверждено замером инспектора; строчными' },
  toneTitle: { id: 'widget.prescriptions.tone.title', defaultMessage: 'Цвет — статус случая:', description: 'Первый слой легенды под таблицей: цвет полосы строки = статус случая карантина' },
  markTitle: { id: 'widget.prescriptions.mark.title', defaultMessage: 'Метка:', description: 'Второй слой легенды под таблицей: метка в строке (отдельный признак)' },
  markLate: { id: 'widget.prescriptions.mark.late', defaultMessage: 'срок предписания прошёл', description: 'Легенда: метка «после срока» — срок предписания прошёл, устранения нет; строчными' },
  toneAria: { id: 'widget.prescriptions.tone.aria', defaultMessage: 'Цвет полосы в начале строки', description: 'aria-label легенды цвета случая под таблицей' },
  mapRed: { id: 'widget.prescriptions.map.red', defaultMessage: 'открыто: красный случай', description: 'Легенда карты: на контуре есть открытое предписание на земле с красным статусом' },
  mapYellow: { id: 'widget.prescriptions.map.yellow', defaultMessage: 'открыто: жёлтый — под наблюдением', description: 'Легенда карты: открытые предписания контура — на земле с жёлтым статусом' },
  mapLate: { id: 'widget.prescriptions.map.late', defaultMessage: 'пунктирная обводка — есть предписание после срока', description: 'Легенда карты состояния: пунктирная обводка контура — срок открытого предписания прошёл' },
  mapClosed: { id: 'widget.prescriptions.map.closed', defaultMessage: 'все устранены замером', description: 'Легенда карты: все предписания контура устранены замером' },
  mapClosedSome: { id: 'widget.prescriptions.map.closedSome', defaultMessage: 'есть устранённые замером', description: 'Легенда карты под фильтром «закрытые»: на контуре есть устранённые предписания' },
  stateTitle: { id: 'widget.prescriptions.stateTitle', defaultMessage: 'Состояние предписаний на контуре', description: 'Заголовок легенды карты состояния предписаний' },
  heatTitle: { id: 'widget.prescriptions.heatTitle', defaultMessage: 'Предписаний на контуре за период', description: 'Заголовок легенды тепловой карты: сколько разных предписаний получил контур' },
  heatScope: { id: 'widget.prescriptions.heatScope', defaultMessage: 'Все предписания за период — открытые и устранённые', description: 'Подпись на месте фильтра в виде «Частота»: шкала считает все предписания периода' },
  heatNone: { id: 'widget.prescriptions.heatNone', defaultMessage: 'предписаний не было', description: 'Легенда тепловой карты: контур без предписаний' },
  heatRing: { id: 'widget.prescriptions.heatRing', defaultMessage: 'обводка — проблема вернулась после устранения', description: 'Легенда тепловой карты: тёмная обводка контура — рецидив (новое предписание после устранения замером)' },
  heatCaption: {
    id: 'widget.prescriptions.heatCaption',
    defaultMessage: 'Частота: {contours} с предписаниями; пороги — квантили по контурам области; рецидив на {repeat}',
    description: 'Подпись над тепловой картой; {contours} — «415 контуров», {repeat} — «12 контурах» (число со словом)',
  },
  repeatCount: {
    id: 'widget.prescriptions.repeatCount',
    defaultMessage: '{count, plural, one {# контуре} other {# контурах}}',
    description: 'Число контуров в предложном падеже: «на 12 контурах»; {count} — число',
  },
  stateCaption: { id: 'widget.prescriptions.stateCaption', defaultMessage: 'Карта состояния: {contours}', description: 'Подпись под картой состояния; {contours} — «83 контура»' },
});

const WORK_STATUS_WORD: Record<string, string> = lazyWords({
  reported: m.wsReported,
  in_progress: m.wsInProgress,
  overdue: m.wsOverdue,
  in_window: m.wsInWindow,
  awaiting_window: m.wsAwaiting,
  no_data: m.wsNoData,
});

type Block = Schemas['QPrescriptionsOut'];
type Work = Schemas['QPrescriptionWorkOut'];
export type Filter = 'open' | 'closed' | 'all';
export type View = 'table' | 'map' | 'heat';
type Sort = 'severity' | 'deadline';

const TONE_LEGEND: { tone: CaseTone; label: keyof typeof m }[] = [
  { tone: 'red', label: 'toneRed' },
  { tone: 'yellow', label: 'toneYellow' },
  { tone: 'green', label: 'toneGreen' },
];
const TONE_FILTER: { tone: CaseTone; label: keyof typeof m }[] = [
  { tone: 'red', label: 'fRed' },
  { tone: 'yellow', label: 'fYellow' },
  { tone: 'green', label: 'fGreen' },
];
const PAGE = 10;

const ha = (microha: number | null | undefined): number | null =>
  microha === null || microha === undefined ? null : microha / 1_000_000;

/** Область виджета: цикл, хозяйство, контур или район (пусто). */
export interface PrescriptionsScope {
  /** Ключ агроцикла (`2026:COTTON`). */
  cycle?: string;
  actorId?: string | number;
  contourId?: string | number;
}

export interface PrescriptionsWidgetProps {
  scope: PrescriptionsScope;
  /** Заголовок карточки; по умолчанию «Предписания карантина». */
  title?: string;
  /** Фильтр при открытии; по умолчанию «открытые». */
  defaultFilter?: Filter;
  /** Строк на страницу таблицы (догрузка — тем же шагом); по умолчанию 10. */
  pageSize?: number;
  /** Нет ни одного предписания в области — карточка не рисуется (по умолчанию да). */
  hideWhenEmpty?: boolean;
  /** Колонка «Хозяйство»; по умолчанию скрыта, когда область — хозяйство или контур. */
  showFarm?: boolean;
  /** Вид при открытии (ответ ИИ: карта очагов, частота); по умолчанию «таблица». */
  defaultView?: View;
}

export function PrescriptionsWidget({
  scope,
  title,
  defaultFilter = 'open',
  pageSize = PAGE,
  hideWhenEmpty = true,
  showFarm,
  defaultView = 'table',
}: PrescriptionsWidgetProps) {
  const t = useT();
  const analysis = useAnalysisContext();
  const [filter, setFilter] = useState<Filter>(defaultFilter);
  const [tone, setTone] = useState<CaseTone | null>(null);
  const [sort, setSort] = useState<Sort>('severity');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [view, setView] = useState<View>(defaultView);
  const [pages, setPages] = useState(1);
  const [openRow, setOpenRow] = useState<number | null>(null);
  const scopeKey = `${scope.cycle ?? ''}|${scope.actorId ?? ''}|${scope.contourId ?? ''}`;
  // Поиск уходит на сервер после паузы набора: страница — из отфильтрованных.
  useEffect(() => {
    const id = window.setTimeout(() => setQ(query.trim()), 300);
    return () => window.clearTimeout(id);
  }, [query]);
  useEffect(() => { setPages(1); setOpenRow(null); }, [filter, tone, sort, q, scopeKey, analysis.key]);
  const single = scope.contourId !== undefined;
  const farmCol = showFarm ?? (scope.actorId === undefined && !single);

  const res = useRequest<Block>(
    () =>
      request<Block>('/quarantine/prescriptions', {
        ...analysis.params,
        ...(scope.cycle ? { cycle: scope.cycle } : {}),
        ...(scope.actorId !== undefined ? { actor_id: String(scope.actorId) } : {}),
        ...(scope.contourId !== undefined ? { contour_id: String(scope.contourId) } : {}),
        status: filter,
        ...(tone ? { tone } : {}),
        ...(q ? { q } : {}),
        sort,
        limit: pageSize * pages,
        offset: 0,
      }),
    [analysis.key, scopeKey, filter, tone, q, sort, pages, pageSize],
  );
  // Прошлая страница остаётся на экране, пока догружается следующая.
  const [last, setLast] = useState<Block | null>(null);
  useEffect(() => { if (res.data) setLast(res.data); }, [res.data]);
  const data = res.data ?? last;

  const heat = view === 'heat' && !single;
  const layer: { data: MapLayerData; ids: string[]; ringIds: string[]; ringDashed: boolean; ringLabel: string; caption: string } | null = useMemo(() => {
    if (!data) return null;
    if (heat) {
      // Частота — за весь период области, без фильтра состояния.
      const rows = data.contours.filter((c) => c.requirements > 0);
      const bins = heatBins(rows.map((c) => c.requirements));
      const byId: Record<string, string> = {};
      for (const c of rows) {
        const bin = heatBinOf(c.requirements, bins);
        if (bin) byId[String(c.contour_id)] = bin.tone;
      }
      const ringIds = rows.filter((c) => (c.recurrences ?? 0) > 0).map((c) => String(c.contour_id));
      return {
        ids: rows.map((c) => String(c.contour_id)),
        ringIds,
        ringDashed: false,
        ringLabel: t(m.heatRing),
        caption: t(m.heatCaption, { contours: countOf(rows.length, WORDS.contour), repeat: t(m.repeatCount, { count: ringIds.length }) }),
        data: {
          values: {
            title: t(m.heatTitle),
            byId,
            legend: bins.map((bin) => ({ key: bin.tone, label: binLabel(bin), tone: bin.tone })),
            noneLabel: t(m.heatNone),
          },
        },
      };
    }
    const byId: Record<string, string> = {};
    for (const c of data.contours) {
      const own = contourTone(c, filter);
      // Фильтр цвета действует и на карту: «красные» — только красные контуры.
      if (own && (tone === null || own === tone)) byId[String(c.contour_id)] = own;
    }
    const ids = Object.keys(byId);
    // Просрочка — отдельный признак: пунктир у контуров с открытым предписанием после срока.
    const ringIds = filter === 'closed'
      ? []
      : data.contours.filter((c) => (c.overdue_requirements ?? 0) > 0 && byId[String(c.contour_id)] && byId[String(c.contour_id)] !== 'green').map((c) => String(c.contour_id));
    return {
      ids,
      ringIds,
      ringDashed: true,
      ringLabel: t(m.mapLate),
      caption: t(m.stateCaption, { contours: countOf(ids.length, WORDS.contour) }),
      data: {
        values: {
          title: t(m.stateTitle),
          byId,
          legend: [
            { key: 'red', label: t(m.mapRed), tone: 'critical' },
            // Жёлтый — жёлтый шкалы (`progress`), а не оранжевый `warning`:
            // оранжевые точки на районе не отличались от красных (agroai-0e8).
            { key: 'yellow', label: t(m.mapYellow), tone: 'progress' },
            { key: 'green', label: t(filter === 'closed' ? m.mapClosedSome : m.mapClosed), tone: 'ramp-2' },
          ],
        },
      },
    };
  }, [data, filter, tone, heat, t]);

  if (failed(res) && !data) return <LoadError compact what={t(m.loadWhat)} error={res.error!} onRetry={res.reload} />;
  if (!data) {
    return (
      <Card size="lg" title={title ?? t(m.title)}>
        <SkeletonList label={t(m.loadWhat)} items={3} tone="soft" />
      </Card>
    );
  }
  const c = data.counters;
  if (hideWhenEmpty && c.works_total === 0) return null;
  // Итог земли — точностью метрики (десятые), как плитка рядом и ИИ (№1653).
  const signal = landHaText(ha(c.signal_microha));
  const loadingMore = res.state === 'loading';
  const narrowed = tone !== null || q !== '';
  const cols = 6 + (farmCol ? 1 : 0);
  // Шапка и фильтр «Все» — одна основа (agroai-0e8): фильтр считает
  // предписания, шапка — работы; одно предписание на частях контура у разных
  // хозяйств даёт несколько работ, и тогда шапка называет обе единицы.
  const reqsTotal = c.open_requirements + c.eliminated_requirements;
  const worksText = countOf(c.works_total, WORDS.work);
  const headCount = c.works_total === reqsTotal ? worksText : t(m.headCount, { works: worksText, reqs: reqsTotal });

  /** Этап случая коротко — где он сейчас (раскрытие покажет все этапы). */
  const stageText = (w: Work): string => {
    const stage = caseStage(w);
    if (stage === 'resolved') return t(m.stEliminated, { date: dateShort(w.eliminated_on!) });
    if (stage === 'followup') return t(m.stFollowup, { date: dateShort(w.followup_on!), outcome: FOLLOWUP_LABEL[w.followup_outcome ?? ''] ?? w.followup_outcome ?? '' });
    if (w.case_state === 'awaiting_decision') return t(m.stDecision);
    if (w.case_state === 'no_addressee') return t(m.stNoAddressee);
    if (stage === 'report') return t(m.stReported);
    return t(m.stRequirement);
  };

  return (
    <Card size="lg" className="crx" title={title ?? t(m.title)} info={data.note} aside={<Badge tone="neutral">{headCount}</Badge>}>
      <p className="crx-summary">
        {t(m.summary, {
          open: num(c.open_requirements),
          closed: num(c.eliminated_requirements),
          hasSignal: signal ? 'yes' : 'no',
          signal: signal ?? '',
          hasOverdue: c.overdue_units > 0 ? 'yes' : 'no',
          // Участки (контур или часть), как говорит ИИ по этому же виджету;
          // плитка «После срока» экрана «Карантин» считает контуры (№1651).
          overdue: c.overdue_units,
          hasPct: c.eliminated_on_time_area_pct !== null ? 'yes' : 'no',
          // Доля — одним знаком, как метрика ИИ и плитка (№1654): не «95» при «94,7».
          pct: c.eliminated_on_time_area_pct === null ? '' : pctValue(c.eliminated_on_time_area_pct),
        })}
      </p>
      {scope.cycle ? <p className="t-caption crx-basis">{t(m.basisCycle)}</p> : null}
      <div className="crx-controls">
        <div className="crx-controls__filters">
          {heat ? <span className="t-caption">{t(m.heatScope)}</span> : (
            <>
              <div className="seg" role="group" aria-label={t(m.filterAria)}>
                {([['open', m.fOpen, c.open_requirements], ['closed', m.fClosed, c.eliminated_requirements], ['all', m.fAll, c.open_requirements + c.eliminated_requirements]] as const).map(([id, label, count]) => (
                  <button key={id} type="button" className={`seg__btn focus-ring ${filter === id ? 'is-on' : ''}`} aria-pressed={filter === id} onClick={() => setFilter(id)}>
                    {t(label)} <span className="seg__count num">{num(count)}</span>
                  </button>
                ))}
              </div>
              <div className="crx-chips" role="group" aria-label={t(m.toneFilterAria)}>
                {TONE_FILTER.map((row) => {
                  const count = data.tones?.[row.tone] ?? 0;
                  const on = tone === row.tone;
                  return (
                    <button
                      key={row.tone}
                      type="button"
                      data-tone={row.tone}
                      className={`crx-chip focus-ring ${on ? 'is-on' : ''}`}
                      aria-pressed={on}
                      disabled={count === 0 && !on}
                      onClick={() => setTone(on ? null : row.tone)}
                    >
                      <i aria-hidden="true" />{t(m[row.label])} <span className="num">{num(count)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="seg" role="group" aria-label={t(m.viewAria)}>
          <button type="button" className={`seg__btn focus-ring ${view === 'table' ? 'is-on' : ''}`} aria-pressed={view === 'table'} onClick={() => setView('table')}>
            <Icon icon={List} size={16} /> {t(m.vTable)}
          </button>
          <button type="button" className={`seg__btn focus-ring ${view === 'map' ? 'is-on' : ''}`} aria-pressed={view === 'map'} onClick={() => setView('map')}>
            <Icon icon={MapIcon} size={16} /> {t(m.vMap)}
          </button>
          {single ? null : (
            <button type="button" className={`seg__btn focus-ring ${view === 'heat' ? 'is-on' : ''}`} aria-pressed={view === 'heat'} onClick={() => setView('heat')}>
              <Icon icon={Flame} size={16} /> {t(m.vHeat)}
            </button>
          )}
        </div>
      </div>
      {/* Поиск и порядок — когда строк больше страницы (или фильтр уже сужен);
          у одного контура их нет: там строк единицы. */}
      {view === 'table' && !single && (c.works_total > pageSize || narrowed || query !== '') ? (
        <div className="crx-controls crx-controls--sub">
          <label className="searchbox crx-search">
            <span className="searchbox__field">
              <Icon icon={Search} size={16} />
              <input type="search" placeholder={t(farmCol ? m.searchPh : m.searchPhNoFarm)} aria-label={t(m.searchAria)} value={query} onChange={(e) => setQuery(e.target.value)} />
            </span>
          </label>
          <div className="seg" role="group" aria-label={t(m.sortAria)}>
            {([['severity', m.sortSeverity], ['deadline', m.sortDeadline]] as const).map(([id, label]) => (
              <button key={id} type="button" className={`seg__btn focus-ring ${sort === id ? 'is-on' : ''}`} aria-pressed={sort === id} onClick={() => setSort(id)}>
                {t(label)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {view !== 'table' && layer ? (
        <div className="crx-map">
          <MapWidget
            colorBy="values"
            data={layer.data}
            filterIds={layer.ids}
            focusIds={layer.ids}
            ringIds={layer.ringIds}
            ringLabel={layer.ringLabel}
            ringDashed={layer.ringDashed}
            points={heat ? undefined : 'all'}
            mode="frame"
            height={420}
            legend="below"
            caption={layer.caption}
          />
        </div>
      ) : data.works.length === 0 ? (
        <div className="crx-empty">
          <p className="t-caption">{t(m.none)}</p>
          {narrowed ? (
            <button type="button" className="linkish focus-ring" onClick={() => { setTone(null); setQuery(''); setQ(''); }}>{t(m.reset)}</button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="crx-table-wrap">
            <table className="astra-table crx-table">
              <thead>
                <tr>
                  <th scope="col" className="crx-table__toggle"><span className="visually-hidden">{t(m.colToggle)}</span></th>
                  <th scope="col">{t(m.colContour)}</th>
                  {farmCol ? <th scope="col">{t(m.colFarm)}</th> : null}
                  <th scope="col">{t(m.colObject)}</th>
                  <th scope="col">{t(m.colStage)}</th>
                  <th scope="col">{t(m.colDeadline)}</th>
                  <th scope="col" className="num">{t(m.colArea)}</th>
                </tr>
              </thead>
              <tbody>
                {data.works.map((w) => {
                  const open = openRow === w.obligation_id;
                  const cls = classShort(w.object_class as never);
                  const area = haText(ha(w.signal_microha));
                  const stage = caseStage(w);
                  return (
                    <Fragment key={w.obligation_id}>
                      <tr data-tone={caseTone(w)} className={open ? 'is-open' : undefined}>
                        <td className="crx-table__toggle">
                          <button
                            type="button"
                            className="crx-toggle focus-ring"
                            aria-expanded={open}
                            aria-label={t(m.rowToggle, { no: w.contour_no })}
                            onClick={() => setOpenRow(open ? null : w.obligation_id)}
                          >
                            <Icon icon={ChevronRight} size={16} />
                          </button>
                        </td>
                        <td className="num">
                          <Link to={`/contours/${w.contour_id}${analysis.linkSearch({ tab: 'quarantine' })}`}>{t(m.contourNo, { no: w.contour_no })}</Link>
                          {w.part_label ? <div className="t-caption">{t(m.partOf, { part: w.part_label })}</div> : null}
                        </td>
                        {farmCol ? <td>{w.actor_name ? <Link to={`/farms/${w.actor_id}${analysis.linkSearch()}`}>{w.actor_name}</Link> : '—'}</td> : null}
                        <td>
                          {w.problem_name ?? <span className="muted">{t(m.objectUnnamed)}</span>}
                          {cls ? <> <Badge tone={isQuarantineClass(w.object_class) ? 'copper' : 'outline'}>{cls}</Badge></> : null}
                        </td>
                        <td>
                          <span className={`crx-stage crx-stage--${stage}`}>{stageText(w)}</span>
                          {stage === 'requirement' ? <div className="t-caption">{WORK_STATUS_WORD[w.work_status] ?? w.work_status}</div> : null}
                        </td>
                        <td className="num">
                          {w.deadline ? dateShort(w.deadline) : <span className="muted">{t(m.noDeadline)}</span>}
                          {w.overdue ? (
                            <div><span className="crx-late"><Icon icon={AlarmClock} size={16} />{w.overdue_days ? t(m.lateDays, { days: w.overdue_days }) : t(m.overdue)}</span></div>
                          ) : null}
                        </td>
                        <td className="num">{area ?? '—'}</td>
                      </tr>
                      {open ? (
                        <tr className="crx-detail" data-tone={caseTone(w)}>
                          <td colSpan={cols}>
                            <PrescriptionCase work={w} workStatusWord={WORK_STATUS_WORD[w.work_status] ?? w.work_status} showFarm={farmCol} onContour={single} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ul className="crx-tones" aria-label={t(m.toneAria)}>
            <li className="crx-tones__head">{t(m.toneTitle)}</li>
            {TONE_LEGEND.map((row) => (
              <li key={row.tone} data-tone={row.tone}><i aria-hidden="true" />{t(m[row.label])}</li>
            ))}
            <li className="crx-tones__head crx-tones__sep">{t(m.markTitle)}</li>
            <li><span className="crx-late"><Icon icon={AlarmClock} size={16} />{t(m.overdue)}</span>{t(m.markLate)}</li>
          </ul>
          <div className="crx-foot">
            <span className="t-caption">{t(m.shown, { shown: num(data.works.length), total: num(data.works_matching), bySeverity: sort === 'severity' ? 'yes' : 'no' })}</span>
            {data.works_matching > data.works.length ? (
              <button type="button" className="astra-button" disabled={loadingMore} onClick={() => setPages((p) => p + 1)}>
                {t(m.more, { count: num(Math.min(pageSize, data.works_matching - data.works.length)) })}
              </button>
            ) : null}
          </div>
        </>
      )}
    </Card>
  );
}
